import { useMemo, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { groupBy } from 'lib/array/arrayUtils'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import CruiseShipAmenitiesItem from './CruiseShipAmenitiesItem'
import CruiseShipAmenitiesTab from './CruiseShipAmenitiesTab'

const AmenitiesGrid = styled.div`
  display: grid;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

interface Props {
  ship: App.CruiseShip
}

function CruiseShipAmenities({ ship }: Props) {
  const [selectedAmenityCategory, setSelectedAmenityCategory] = useState<string | null>(null)

  const amenities: Array<App.CruiseShipAmenity> = useMemo(() => {
    const crawledAmenities = ship.amenities || []
    if (crawledAmenities.length) {
      return crawledAmenities
    }

    const normalAmenities = (ship.shipInfo || [])
      .map((shipInfo) => {
        if (!shipInfo?.amenityInfo) return []

        return shipInfo.amenityInfo.amenities.map((amenity) => ({
          id: amenity.title,
          title: amenity.title,
          description: amenity.description || '',
          category: shipInfo.amenityInfo?.title as string,
          listItems: amenity.listItems,
        }))
      }).flat()

    return normalAmenities
  }, [ship.shipInfo, ship.amenities])

  const amenitiesByCategory = useMemo(
    () => groupBy(amenities, (amenity) => amenity.category),
    [amenities],
  )

  const categories = useMemo(() => (
    // keep dining tab in first position
    (Array.from(amenitiesByCategory.keys())
      .map((category) => ({
        category,
        order: category.toLowerCase().includes('dining') ? 0 : 1,
      }))
      .sort((a, b) => a.order - b.order)
      .map((item) => item.category))
  ), [amenitiesByCategory])

  const selectedAmenities = useMemo(
    () => amenitiesByCategory.get(selectedAmenityCategory!) || [],
    [amenitiesByCategory, selectedAmenityCategory],
  )

  useEffect(() => {
    if (categories.length) {
      setSelectedAmenityCategory(categories[0])
    }
  }, [categories])

  if (!categories.length) {
    return null
  }

  return <Group direction="vertical" gap={16}>
    <Heading variant="heading3">
      <FormattedMessage
        id="K6PyN7eKF8VpmUJY70UA"
        defaultMessage="Facilities"
        description="Heading for cruise ship facilities section in the cruise section"
      />
    </Heading>

    <HorizontalContainerWithFadedOverflow gap={16}>
      {categories.map((category) => (
        <CruiseShipAmenitiesTab
          key={`tab-amenity-${category}`}
          category={category}
          isSelected={selectedAmenityCategory === category}
          onSelect={setSelectedAmenityCategory}
        />
      ))}
    </HorizontalContainerWithFadedOverflow>

    {!!selectedAmenities && (
      <AmenitiesGrid>
        {selectedAmenities.map((amenity: App.CruiseShipAmenity) => (
          <CruiseShipAmenitiesItem key={`amenity-item-${amenity.title}`} amenity={amenity} />
        ))}
      </AmenitiesGrid>
    )}
  </Group>
}

export default CruiseShipAmenities
